import React, {useEffect, useState} from 'react';
import {Button, Checkbox, Divider, Drawer, IconButton, Stack, Typography} from '@mui/material';
import {Outlet, useNavigate, useParams} from 'react-router-dom';
import {useTranslation} from 'react-i18next';
import {useSnackbar} from 'notistack';
import {AddWhite, Close} from '@icons/index';
import {Sizes} from '@constants/sizes.constants';
import SelectComponent, {SelectItem} from '@components/inputs/select/Select.component';
import TextFieldComponent from '@components/inputs/textField/TextField.component';
import {finalize, map} from 'rxjs';
import {clientsService} from '@store/clients';
import {ManageUser, Roles, UserPreferences, usersService} from '@store/settings/users';
import AsyncMultiSelectWithSearchComponent
  from '@components/inputs/asyncMultiSelect/AsyncMultiSelectWithSearch.component';
import {OrganisationLanguage} from '@store/common/organisation.enums';
import {clientsUnitsService} from '@store/clients/units';
import AsyncSelectWithSearchComponent from '@components/inputs/asyncSelect/AsyncSelectWithSearch.component';
import {suppliersService} from '@store/suppliers';
import {suppliersUnitsService} from '@store/suppliers/units';
import MultiSelectComponent from '@components/inputs/multiSelect/MultiSelect.component';

const ManageUserDrawer = () => {

  const {t} = useTranslation();
  const navigate = useNavigate();
  const {enqueueSnackbar} = useSnackbar();
  const {userId} = useParams();
  const [isCreation, setIsCreation] = useState(false);

  const [loading, setLoading] = useState(false);

  const [user, setUser] = useState<ManageUser>({});

  const updateUser = (name: string) => (value: any) => setUser((state) => ({...state, [name]: value}));

  useEffect(() => {
    if (userId) {
      if (userId === 'new') {
        setIsCreation(true);
        updateUser('userPreferences')(UserPreferences.selectItems().map(up => up.value));
      } else {
        usersService.getUserById(userId).subscribe({
          next: setUser,
          error: (err) => enqueueSnackbar(err.text, err.options),
        });
      }

    }
  }, [enqueueSnackbar, userId]);

  const handleClose = () => navigate('..', {replace: true});

  const handleSave = () => {
    if (!userId) return;
    setLoading(true);
    if (isCreation) {
      usersService.createUser(user).pipe(finalize(() => setLoading(false))).subscribe({
        next: (user) => {
          enqueueSnackbar(t('settings.users.success.created'), {variant: 'success'});
          if (user.id) {
            navigate(`../${user.id}`, {replace: true});
          }
        },
        error: (err) => {
          switch (err.text) {
            case 'USER_ALREADY_EXISTS':
              enqueueSnackbar(t(err.text), err.options);
              break;
            default:
              enqueueSnackbar(err.text, err.options);
          }

        },
      });
    } else {
      usersService.saveUser(user, userId).pipe(finalize(() => setLoading(false))).subscribe({
        next: (user) => {
          setUser(user);
          enqueueSnackbar(t('settings.users.success.modified'), {variant: 'success'});
          // handleClose();
        },
        error: (err) => enqueueSnackbar(err.text, err.options),
      });
    }

  };

  const addOrganisation = () => {
    setUser({
      ...user,
      organisationAndUnits: [
        ...(user.organisationAndUnits || []),
        {id: undefined, name: '', units: undefined}
      ]
    });
  };


  const updateUserOrganisation = (item: SelectItem, index: number) => {
    if (user.organisationAndUnits) {
      let userOrganisation = user.organisationAndUnits[index];
      const newUser = {...user};

      if (!item) {
        newUser.organisationAndUnits?.splice(index, 1);
        setUser(newUser);
      } else if (item.value !== userOrganisation.id) {
        userOrganisation = {
          id: item.value,
          name: item.label,
          units: [],
        };
        newUser.organisationAndUnits![index] = userOrganisation;
        setUser(newUser);
      }
    }
  };

  const updateUserOrganisationUnit = (items: SelectItem[], orgIndex: number) => {
    if (user.organisationAndUnits) {
      const newUser = {...user};

      newUser.organisationAndUnits![orgIndex].units = items.map((i) => ({id: i.value, name: i.label}));
      setUser(newUser);
    }
  };

  return (
    <>
      <Drawer anchor="right" open onClose={handleClose}>
        <Stack overflow="auto">
          <Stack direction="row" justifyContent="space-between">
            <Typography fontSize={24} fontWeight="700"> {t('settings.users.manage.title')}</Typography>
            <IconButton onClick={handleClose}>
              <Close/>
            </IconButton>
          </Stack>
          <Stack pt="27px" pr="10px" spacing={2}>
            <SelectComponent
              labelWidth={Sizes.drawerInputLabelWidth}
              label={t('settings.users.manage.language')}
              handleChange={updateUser('language')}
              items={OrganisationLanguage.selectItems()}
              value={user.language}/>
            <MultiSelectComponent labelWidth={Sizes.drawerInputLabelWidth}
                                  label={t('settings.users.manage.preferredMail')} items={UserPreferences.selectItems()}
                                  values={user.userPreferences || []} handleChange={updateUser('userPreferences')}/>
            <Stack direction={'row'} alignItems={'center'} justifyContent={'space-between'}>
              <Typography>{t('settings.users.manage.statsAccess')}</Typography>
              <Checkbox checked={user.hasStatsAccess === true}
                        onClick={() => updateUser('hasStatsAccess')(!user.hasStatsAccess)}/>
            </Stack>


          </Stack>
          <Divider variant="fullWidth" orientation="horizontal" sx={{paddingTop: '74px'}}/>
          <Stack pt="27px" pr="10px" spacing={2}>
            <Typography fontSize={16} fontWeight="700"> {t('settings.users.manage.details')}</Typography>
            <TextFieldComponent
              labelWidth={Sizes.drawerInputLabelWidth}
              label={t('settings.users.manage.lastName')}
              placeholder={t('global.selectOption')}
              value={user.lastName ?? ''}
              handleChange={updateUser('lastName')}
              readOnly={!isCreation}
              required
            />
            <TextFieldComponent
              labelWidth={Sizes.drawerInputLabelWidth}
              label={t('settings.users.manage.firstName')}
              placeholder={t('global.selectOption')}
              value={user.firstName ?? ''}
              handleChange={updateUser('firstName')}
              readOnly={!isCreation}
              required/>
            <TextFieldComponent
              labelWidth={Sizes.drawerInputLabelWidth}
              label={t('settings.users.manage.email')}
              placeholder={t('global.selectOption')}
              handleChange={updateUser('email')}
              value={user.email ?? ''}
              readOnly={!isCreation}
              required/>
          </Stack>
          <Divider variant="fullWidth" orientation="horizontal" sx={{paddingTop: '32px'}}/>
          <Stack pt="27px" pr="10px" spacing={3}>
            <Typography fontSize={16} fontWeight="700"> {t('settings.users.manage.professional')}</Typography>
            <SelectComponent
              labelWidth={Sizes.drawerInputLabelWidth}
              label={t('settings.users.manage.type')}
              placeholder={t('global.selectOption')}
              items={Roles.selectItems()}
              handleChange={(value) => {
                setUser({...user, roles: value ? [value] : [], organisationAndUnits: []});
              }}
              value={user.roles?.length ? user.roles[0] : ''}/>
          </Stack>
          <Divider variant="fullWidth" orientation="horizontal" sx={{paddingTop: '32px'}}/>
          <Stack pt="27px" pr="10px" spacing={3}>
            <Stack direction="row" alignItems="center" justifyContent="space-between">
              <Typography fontSize={16} fontWeight="700"> {t('settings.users.manage.organisation')}</Typography>
              {user.roles?.length ?
                <IconButton aria-label="small-square-text" centerRipple={false} onClick={() => addOrganisation()}>
                  <AddWhite/>
                </IconButton> : null
              }
            </Stack>
            <Stack spacing={3}>
              {user.roles?.length ? user.organisationAndUnits?.map((o, index) => (
                <Stack spacing={2} key={o.id || index}>
                  <AsyncSelectWithSearchComponent
                    labelWidth={Sizes.drawerInputLabelWidth}
                    label={t('settings.users.manage.organisations')}
                    placeholder={t('global.selectOption')}
                    handleChange={(item) => updateUserOrganisation(item, index)}
                    value={o ? {label: o.name || '', value: o.id} : undefined}
                    getOptions={(search) => {
                      return user.roles![0] !== Roles.HAULING ? clientsService.searchClients(search).pipe(
                        map((options) => options.data.map((o) => ({label: o.name || '-', value: o.id}))),
                      ) : suppliersService.searchSuppliers(search).pipe(
                        map((options) => options.data.map((o) => ({label: o.name || '-', value: o.id})))
                      );
                    }}/>
                  {o.id && <AsyncMultiSelectWithSearchComponent
                      labelWidth={Sizes.drawerInputLabelWidth}
                      label={t('settings.users.manage.units')}
                      placeholder={t('global.selectOptions')}
                      handleChange={(items) => updateUserOrganisationUnit(items, index)}
                      values={o.units?.map((unit): SelectItem => {
                        return {
                          label: unit.name || '',
                          value: unit.id
                        };
                      }) ?? undefined}
                      getOptions={(search) => {
                        return user.roles![0] !== Roles.HAULING ? clientsUnitsService.searchClientsUnits(search, o.id ? [o.id] : undefined).pipe(
                          map((options) => options.data.map((o) => ({label: o.name || '-', value: o.id}))),
                        ) : suppliersUnitsService.searchSuppliersUnits(search, o.id ? [o.id] : undefined).pipe(
                          map((options) => options.data.map((o) => ({label: o.name || '-', value: o.id}))),
                        );
                      }}/>}
                </Stack>
              )) : <Typography>{t('settings.users.manage.selectRoleWarning')}</Typography>}
            </Stack>
          </Stack>
          <Divider variant="fullWidth" orientation="horizontal" sx={{paddingTop: '32px'}}/>
          <Stack alignSelf="flex-end" pb="25px" pt="53px" pr="10px">
            <Button variant="contained" color="primary" disabled={loading} onClick={handleSave}>
              {t('global.save')}
            </Button>
          </Stack>
        </Stack>
      </Drawer>
      <Outlet/>
    </>
  );
};

export default ManageUserDrawer;
